import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostListener, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { forkJoin, Observable, of, Subscription, catchError, switchMap } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';

import { MediaContainer, PageSlotModule } from '@spartacus/storefront';
import { WindowRef } from '@spartacus/core';

import { SdArgumentsComponent, IArguments, ArgumentsLayoutEnum } from '@seb/web-design-system/arguments';
import { ISeo, SdSeoWrapperComponent } from '@seb/web-design-system/seos';
import { SdPushSliderComponent } from '@seb/web-design-system/push-slider';
import { ITeaser, SdTeaserComponent } from '@seb/web-design-system/teaser';

import {
  BrandThemesEnum, IComponentWrapper, IExtendedLink, IHeading, ILinkMedia, ImageDsService, IPushCard, IPushCardWithLayout,
  PositionEnum, ScrollSpyService, SdComponentWrapperComponent, SdPushCardComponent, SdThemeWrapperComponent,
  SizesEnum, VideoDsService, CoreModalService, ImageRatioEnum, ThemesEnum, PushSizeEnum
} from '@seb/web-design-system/core';

import { ArgumentMapper, BrandCornerMapper, HeadingParagraphMapper, PushCardMapper, TeaserMapper, toHeading } from '@seb/web-common/core/mappers';
import { StickyProductComparatorComponent } from '../../components/sticky-product-comparator/sticky-product-comparator.component';
import { CategoryAdditionalMarketingDataPositionEnum, CategoryAdditionalMarketingDataTypeEnum } from '@seb/web-common/core/enums';
import { ProductCardGridComponent } from '@seb/web-common/core/components/static/product-card-grid/product-card-grid.component';
import { ChatService, ImageService, LazyLoadingService, RecipeService, TrackingService } from '@seb/web-common/core/services';
import { BaseRecipeListComponent } from '@seb/web-common/core/components/base/base-recipe-list/base-recipe-list.component';
import { BaseComparatorComponent } from '@seb/web-common/core/components/base/base-comparator/base-comparator.component';
import { CustomBaseStore, CustomMedia, FaqEntry, Recipe, SubCategoryPageConfig } from '@seb/web-common/core/models';
import { AbstractCategoryPageComponent } from '../abstract-category-page/abstract-category-page.component';
import { BaseFaqComponent } from '@seb/web-common/core/components/base/base-faq/base-faq.component';
import { IInfoBanner, SdInfoBannerComponent } from '@seb/web-design-system/info-banner';
import { CustomJsonLdDirective } from '@seb/web-common/core/directives';
import { RecipeUserEnum } from '@seb/web-design-system/recipe-user';
import { IRecipeCard } from '@seb/web-design-system/recipe-card';

@Component({
  selector: 'wc-sub-category-page',
  templateUrl: './sub-category-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdComponentWrapperComponent,
    SdPushCardComponent,
    SdPushSliderComponent,
    SdTeaserComponent,
    ProductCardGridComponent,
    SdThemeWrapperComponent,
    PageSlotModule,
    BaseComparatorComponent,
    SdArgumentsComponent,
    BaseRecipeListComponent,
    BaseFaqComponent,
    SdSeoWrapperComponent,
    StickyProductComparatorComponent,
    CustomJsonLdDirective,
    SdInfoBannerComponent
  ],
  providers: [CoreModalService]
})
export class SubCategoryPageComponent extends AbstractCategoryPageComponent implements OnInit, AfterViewInit, OnDestroy {
  ArgumentsLayoutEnum = ArgumentsLayoutEnum;
  BrandThemesEnum = BrandThemesEnum;
  PushSizeEnum = PushSizeEnum;
  PositionEnum = PositionEnum;
  ThemesEnum = ThemesEnum;
  SizesEnum = SizesEnum;

  pushLame1: IPushCard;
  contentLame1: IComponentWrapper;
  headingLame4: IHeading;
  pushsLame4: IPushCardWithLayout[];
  contentLame4: IComponentWrapper;
  headingLame5: IHeading;
  productCodesLame5: string[];
  contentLame5: IComponentWrapper;
  pushLame7: IPushCard;
  argumentsLame7: IArguments;
  argumentsLame8: IArguments;
  headingLame9: IHeading;
  recipesIdListLame9: string[];
  contentLame9: IComponentWrapper;
  argumentsLame10: IArguments;
  faqs: FaqEntry[];
  faqWrapper: IComponentWrapper;
  seo: ISeo;
  promotionPushHasBeenViewed = false;
  promotionName: string;
  recipes: IRecipeCard[];
  recipesSize: number;
  teasers: ITeaser[] = [];
  textConfig: PositionEnum.left | PositionEnum.right | PositionEnum.center = PositionEnum.center;
  lameConfig: SubCategoryPageConfig;
  size: SizesEnum.l | SizesEnum.m | SizesEnum.s = SizesEnum.l;
  lamesMediaSubscriptions: { [key: string]: Subscription } = {};
  isRecipesEnabled = true;
  isDescriptionLame1: boolean;
  isFitLame1: boolean;
  hasModalLame1: boolean;
  isDescriptionLame7: boolean;
  isFitLame7: boolean;
  hasModalLame7: boolean;
  gridLoaded = false;
  lame1Loaded = false;
  useLazyLoad = false;
  hideSeo = false;
  lame1ThemeConfig: string;
  teaserConfig: boolean;
  snippetImages = signal<any[]>([]);
  snippetVideos = signal<any[]>([]);
  isBannerVisible = false;
  bannerData: IInfoBanner = {};
  brandSite: string;

  readonly productCardGridContext = 'Product List';
  readonly comparatorContext = 'Product Comparator';
  private baseStore: CustomBaseStore;

  @ViewChild('pushCardComponent', { read: ElementRef })
  pushCardComponent: ElementRef;

  constructor(
    private readonly scrollSpyService: ScrollSpyService,
    private readonly pushCardMapper: PushCardMapper,
    private readonly translateService: TranslateService,
    private readonly argumentMapper: ArgumentMapper,
    private readonly trackingService: TrackingService,
    private readonly recipeService: RecipeService,
    private readonly imageService: ImageService,
    private readonly chatService: ChatService,
    private readonly route: ActivatedRoute,
    private readonly lazyLoadingService: LazyLoadingService,
    private readonly teaserMapper: TeaserMapper,
    private readonly brandCornerMapper: BrandCornerMapper,
    private readonly headingParagraphMapper: HeadingParagraphMapper,
    private readonly imageDsService: ImageDsService,
    private readonly videoDsService: VideoDsService,
    private readonly windowRef: WindowRef
  ) {
    super();

    this.isRecipesEnabled = this.brandCornerMapper.toWcConfig(this.brandCorner)?.isRecipesEnabled ?? this.isRecipesEnabled;
    this.brandSite = this.baseStoreService.baseStore.brandSite;
    this.useLazyLoad = this.lazyLoadingService.isEnabled;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.promotionPushHasBeenViewed && this.pushLame7) {
      this.promotionName = this.pushLame7.richParagraph.heading.data.title
        ? this.pushLame7.richParagraph.heading.data.title
        : undefined;
      this.promotionPushHasBeenViewed = this.trackingService.checkingForPromoView(
        this.pushCardComponent,
        true,
        this.promotionName
      );
    }
  }

  cleanSubscriptions(): void {
    for (const subscription of Object.keys(this.lamesMediaSubscriptions)) {
      this.lamesMediaSubscriptions[subscription].unsubscribe();
    }
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.subscriptions.add(
      this.videoDsService.loadVideoSnippets(this.brandSite).subscribe((videos) => {
        this.snippetVideos.set(videos);
      })
    );

    this.setConfig();

    this.faqWrapper = {
      heading: this.headingParagraphMapper.toHeadingParagraph({
        heading: {
          headline: this.translateService.instant('categoryPage.sectionTitles.faq')
        },
        titleTheme: this.lameConfig?.lame11?.titleTheme,
        titleLevel: 2,
        textConfig: this.lameConfig?.lame11?.textConfig || PositionEnum.center
      })
    };

    this.subscriptions.add(
      this.route.queryParamMap.subscribe((queryParams) => {
        this.hideSeo = queryParams.has('page') && +queryParams.get('page') > 1;
        this.cdRef.markForCheck();
      })
    );

    this.subscriptions.add(
      this.imageDsService.loadImageSnippets().subscribe((images) => {
        this.snippetImages.set(images);
      })
    );
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.scrollSpyService.spy({ thresholdBottom: 300, thresholdTop: 60 });
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    if (isPlatformBrowser(this.platformId)) {
      this.scrollSpyService.stopSpying();
    }

    this.cleanSubscriptions();
  }

  protected onCategoryChanged(): void {
    this.cleanSubscriptions();
    this.initLame1();
  }

  protected onBrandCornerChanged(): void {
    this.setConfig();
  }

  onClickLink(event: IExtendedLink | ILinkMedia): void {
    if (event?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
    this.trackingService.sendEvent('clickSubCategoryPushLink', {
      appType: event.linkName
    });
  }

  onPromoClick(): void {
    this.trackingService.onPromoClick(true, this.promotionName);
  }

  onGridLoaded(): void {
    this.gridLoaded = true;

    this.initLame4();
    this.initLame5();
    this.initLame7();
    this.initLame8();
    this.initLame9();
    this.initLame10();
    this.initLame12();

    this.faqs = this.category.faqs?.faqEntries;
    this.cdRef.markForCheck();
  }

  private setConfig(): void {
    const wcConfig = this.brandCornerMapper.toWcConfig(this.brandCorner);

    this.lameConfig = wcConfig.subCategoryPage;
    this.teaserConfig = wcConfig.isTeaserEmbedded;
    this.lame1ThemeConfig = this.lameConfig?.lame1?.theme;
    this.isRecipesEnabled = wcConfig.isRecipesEnabled ?? this.isRecipesEnabled;
  }

  private initLame1(): void {
    this.cleanLame1();

    const dataLame1 = this.category.additionalMarketingDatas?.find((data) =>
      data.position === CategoryAdditionalMarketingDataPositionEnum.Lame_1 &&
      (data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Push_Card_Component ||
        data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Teaser)
    );

    if (dataLame1) {
      switch (dataLame1.typeSection) {
        case CategoryAdditionalMarketingDataTypeEnum.Push_Card_Component:
          this.isDescriptionLame1 = dataLame1.isDescription;
          this.isFitLame1 = dataLame1.isFit;
          this.hasModalLame1 = dataLame1.hasModal;
          if (dataLame1.mediaContainer) {
            const pushCustomMedia = this.getMediaFromAdditionalMarketing(dataLame1.mediaContainer);
            this.lamesMediaSubscriptions['lame1']?.unsubscribe();
            this.lamesMediaSubscriptions['lame1'] = this.getMediaContainer(pushCustomMedia).subscribe((media) => {
              this.pushLame1 = this.pushCardMapper.categoryAdditionalMarketingDatatoIPushCard(
                dataLame1, media, this.brandLogo
              );
              this.lame1Loaded = true;
              this.cdRef.markForCheck();
            });
          } else {
            this.pushLame1 = this.pushCardMapper.categoryAdditionalMarketingDatatoIPushCard(
              dataLame1, undefined, this.brandLogo
            );
            this.lame1Loaded = true;
          }
          break;
        case CategoryAdditionalMarketingDataTypeEnum.Teaser:
          dataLame1.additionalMarketingPushs?.forEach((data, index) => {
            data.brandLogo = { media: this.brandLogo?.media };
            if (data.mediaContainer) {
              const customMedia = this.getMediaFromAdditionalMarketing(data.mediaContainer);
              this.lamesMediaSubscriptions['lame1']?.unsubscribe();
              this.lamesMediaSubscriptions['lame1'] = this.getMediaContainer(customMedia).subscribe((media) => {
                this.teasers[index] = this.teaserMapper.toITeaser(
                  data, this.lame1ThemeConfig, this.teaserConfig, media, this.textConfig
                );
                this.lame1Loaded = true;
                this.cdRef.markForCheck();
              });
            } else {
              this.teasers[index] = this.teaserMapper.toITeaser(
                data, this.lame1ThemeConfig, this.teaserConfig, undefined, this.textConfig
              );
              this.lame1Loaded = true;
            }
          });
          break;
      }

      if (dataLame1?.media) {
        this.contentLame1 = { background: this.imageService.normalizeMedia(dataLame1?.media) };
      }
    } else {
      this.pushLame1 = undefined;
      this.lame1Loaded = true;
    }

    if (this.brandCorner && this.category.name) {
      this.showBrandCornerBanner(this.brandCorner, this.category.name);
    }
  }

  private cleanLame1(): void {
    this.teasers = [];
    this.lamesMediaSubscriptions?.['lame1']?.unsubscribe();
    this.pushLame1 = undefined;
    this.lame1Loaded = false;
    this.isBannerVisible = false;
  }

  private initLame4(): void {
    this.cleanLame4();

    const dataLame4 = this.category?.additionalMarketingDatas?.find((data) =>
      data.position === CategoryAdditionalMarketingDataPositionEnum.Lame_4 &&
      data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Push_Slider_Module
    );

    dataLame4?.additionalMarketingPushs?.forEach((data, index) => {
      if (data.mediaContainer) {
        const customMedia = this.getMediaFromAdditionalMarketing(data.mediaContainer);
        this.lamesMediaSubscriptions['lame4']?.unsubscribe();
        this.lamesMediaSubscriptions['lame4'] = this.getMediaContainer(customMedia).subscribe((media) => {
          const push = this.pushCardMapper.categoryAdditionalMarketingDatatoIPushCard(data, media);
          if (!!push && !!push.richParagraph.heading.data.title && !!push.richParagraph.heading.data.description) {
            this.pushsLame4[index] = {
              push,
              imageRatio: this.lameConfig?.lame4?.pushImageRatio || ImageRatioEnum.r16_9,
              textConfig:
                (this.lameConfig?.lame4?.pushTextConfig as
                  | PositionEnum.left
                  | PositionEnum.right
                  | PositionEnum.center) || PositionEnum.center,
              mediaConfig: this.lameConfig?.lame4?.pushMediaConfig || PositionEnum.top,
              mediaAsBackground: false,
              themesConfig: this.lameConfig?.lame4?.pushTheme || ThemesEnum.light,
              isFit: data.isFit ? data.isFit : false,
              isDescription: data.isDescription ? data.isDescription : false,
              hasModal: data.hasModal ? data.hasModal : false
            };
            this.cdRef.markForCheck();
          }
        });
      }
    });

    this.headingLame4 = !!dataLame4?.headline && this.pushsLame4?.length > 0 ? toHeading(dataLame4) : undefined;

    const richParagraphData = {
      heading: {
        headline: !!dataLame4?.headline && this.pushsLame4?.length > 0 ? dataLame4.headline : undefined
      },
      titleTheme: this.lameConfig?.lame4?.titleTheme || BrandThemesEnum.brand,
      size: this.size,
      titleLevel: 2,
      textConfig: this.lameConfig?.lame4?.titleTextConfig || PositionEnum.center,
      isUnderlined: this.lameConfig?.lame4?.titleUnderlined
    };

    this.contentLame4 = {
      heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData),
      footNotes: [],
      links: []
    };

    if (dataLame4?.mediaContainer) {
      this.contentLame4 = {
        ...this.contentLame4,
        background: this.imageService.normalizeMedia(
          this.imageService.getMediaFromCustomMediaArray(dataLame4?.mediaContainer)
        )
      };
    }
  }

  private cleanLame4(): void {
    this.pushsLame4 = [];
    this.lamesMediaSubscriptions?.['lame4']?.unsubscribe();
  }

  private initLame5(): void {
    const dataLame5 = this.category.additionalMarketingDatas?.find(
      (data) =>
        data.position === CategoryAdditionalMarketingDataPositionEnum.Lame_5 &&
        data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Product_Comparator
    );

    this.productCodesLame5 = dataLame5?.products?.split(' ');
    this.headingLame5 = !!dataLame5?.headline && this.productCodesLame5?.length > 0 ? toHeading(dataLame5) : undefined;

    const richParagraphData = {
      heading: {
        headline: !!dataLame5?.headline && this.pushsLame4?.length > 0 ? dataLame5.headline : undefined
      },
      titleTheme: this.lameConfig?.lame5?.titleTheme || BrandThemesEnum.brand,
      size: this.size,
      titleLevel: 2,
      textConfig: this.lameConfig?.lame5?.titleTextConfig || PositionEnum.center,
      isUnderlined: this.lameConfig?.lame5?.titleUnderlined
    };

    this.contentLame5 = {
      heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData),
      footNotes: [],
      links: []
    };
  }

  private initLame7(): void {
    this.pushLame7 = undefined;
    this.argumentsLame7 = undefined;
    this.lamesMediaSubscriptions?.['lame7']?.unsubscribe();

    let dataLame7 = this.category.additionalMarketingDatas?.find((data) =>
      data.position === CategoryAdditionalMarketingDataPositionEnum.Lame_7 &&
      (data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Push_Card_Component ||
        data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Argument)
    );

    if (dataLame7) {
      dataLame7 = {
        ...dataLame7,
        isUnderlined: this.lameConfig?.lame7.titleUnderlined
      };
    }

    if (dataLame7) {
      switch (dataLame7.typeSection) {
        case CategoryAdditionalMarketingDataTypeEnum.Push_Card_Component:
          if (dataLame7.mediaContainer) {
            this.lamesMediaSubscriptions?.['lame7']?.unsubscribe();
            const customMedia = this.getMediaFromAdditionalMarketing(dataLame7.mediaContainer);
            this.lamesMediaSubscriptions['lame7'] = this.getMediaContainer(customMedia).subscribe((media) => {
              this.pushLame7 = this.pushCardMapper.categoryAdditionalMarketingDatatoIPushCard(dataLame7, media);
              this.cdRef.markForCheck();
            });
          } else {
            this.pushLame7 = this.pushCardMapper.categoryAdditionalMarketingDatatoIPushCard(dataLame7);
          }
          this.isDescriptionLame7 = dataLame7.isDescription;
          this.isFitLame7 = dataLame7.isFit;
          this.hasModalLame7 = dataLame7.hasModal;
          break;
        case CategoryAdditionalMarketingDataTypeEnum.Argument:
          if (dataLame7.mediaContainer) {
            this.lamesMediaSubscriptions?.['lame7']?.unsubscribe();
            const customMedia = this.getMediaFromAdditionalMarketing(dataLame7.mediaContainer);
            this.lamesMediaSubscriptions['lame7'] = this.getMediaContainer(customMedia).subscribe((media) => {
              this.argumentsLame7 = this.argumentMapper.categoryAdditionalMarketingDataToIArgument(dataLame7, media);
              this.cdRef.markForCheck();
            });
          } else {
            this.argumentsLame7 = this.argumentMapper.categoryAdditionalMarketingDataToIArgument(dataLame7);
          }
          break;
      }
    } else {
      this.pushLame7 = undefined;
    }
  }

  private initLame8(): void {
    this.lamesMediaSubscriptions?.['lame8']?.unsubscribe();

    let dataLame8 = this.category.additionalMarketingDatas?.find((data) =>
      data.position === CategoryAdditionalMarketingDataPositionEnum.Lame_8 &&
      data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Argument
    );

    if (dataLame8) {
      dataLame8 = {
        ...dataLame8,
        isUnderlined: this.lameConfig?.lame8.titleUnderlined
      };
    }

    const customMedia = dataLame8?.mediaContainer
      ? this.getMediaFromAdditionalMarketing(dataLame8.mediaContainer)
      : undefined;

    if (customMedia && dataLame8) {
      this.lamesMediaSubscriptions?.['lame8']?.unsubscribe();
      this.lamesMediaSubscriptions['lame8'] = this.getMediaContainer(customMedia).subscribe((media) => {
        this.argumentsLame8 = this.argumentMapper.categoryAdditionalMarketingDataToIArgument(dataLame8, media);
        this.cdRef.markForCheck();
      });
    } else {
      this.argumentsLame8 = dataLame8
        ? this.argumentMapper.categoryAdditionalMarketingDataToIArgument(dataLame8)
        : undefined;
    }
  }

  private initLame9(): void {
    this.recipes = undefined;

    const dataLame9 = this.category.additionalMarketingDatas?.find((data) =>
      data.position === CategoryAdditionalMarketingDataPositionEnum.Lame_9 &&
      data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Recipe_Card
    );

    this.headingLame9 = dataLame9 ? toHeading(dataLame9) : undefined;

    const richParagraphData = {
      heading: {
        headline: dataLame9 ? dataLame9.headline : undefined
      },
      titleTheme: this.lameConfig?.lame9?.titleTheme || BrandThemesEnum.brand,
      size: this.size,
      titleLevel: 2,
      textConfig: this.lameConfig?.lame9?.titleTextConfig || PositionEnum.center
    };

    this.contentLame9 = {
      heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData),
      footNotes: [],
      links: []
    };

    this.recipesIdListLame9 = dataLame9?.recipeList?.split(',');

    if (this.recipesIdListLame9 && this.isRecipesEnabled) {
      this.subscriptions.add(
        forkJoin([
          // eslint-disable-next-line no-unsafe-optional-chaining
          ...this.recipesIdListLame9?.map((recipeId: string) => {
            let sourceSystem: string;
            let id: string;
            const idParts: string[] = recipeId.split(/\/(.+)/);

            if (idParts.length === 3) {
              if (recipeId.startsWith('/v1/variants/')) {
                // (recipe id format '/v1/variants/id')
                sourceSystem = 'PRO';
                id = recipeId.endsWith('/') ? recipeId : recipeId + '/'; // check if the recipeid ends with /
              } else {
                sourceSystem = idParts[0];
                id = idParts[1];
              }
            } else {
              sourceSystem = 'PRO';
              id = recipeId;
            }
            return this.recipeService.getRecipe(sourceSystem, id).pipe(
              catchError(() => of(null)),
              switchMap((recipe: Recipe | null) => (recipe ? this.recipeService.extractRecipe(recipe) : of(null)))
            );
          })
        ]).subscribe((results: IRecipeCard[]): void => {
          if (this.brandLogo) {
            results.forEach((result) => {
              if (result.user.type === RecipeUserEnum.BRAND) {
                result.user.image = this.brandLogo?.media;
                result.user.text = this.brandCorner.charAt(0).toUpperCase() + this.brandCorner.slice(1);
              }
            });
          }
          this.recipes = results.filter((recipe) => recipe !== null);
          this.recipesSize = this.recipes?.length > 0 ? this.recipes.length : 0;
          this.cdRef.markForCheck();
        })
      );
    }
  }

  private initLame10(): void {
    let dataLame10 = this.category.additionalMarketingDatas?.find((data) =>
      data.position === CategoryAdditionalMarketingDataPositionEnum.Lame_10 &&
      data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Argument
    );

    if (dataLame10) {
      dataLame10 = {
        ...dataLame10,
        isUnderlined: this.lameConfig?.lame10.titleUnderlined
      };
    }

    this.lamesMediaSubscriptions['lame10']?.unsubscribe();

    const customMedia = dataLame10?.mediaContainer
      ? this.getMediaFromAdditionalMarketing(dataLame10.mediaContainer)
      : undefined;

    if (customMedia && dataLame10) {
      this.lamesMediaSubscriptions['lame10'] = this.getMediaContainer(customMedia).subscribe((media) => {
        this.argumentsLame10 = this.argumentMapper.categoryAdditionalMarketingDataToIArgument(dataLame10, media);
        this.cdRef.markForCheck();
      });
    } else {
      this.argumentsLame10 = dataLame10
        ? this.argumentMapper.categoryAdditionalMarketingDataToIArgument(dataLame10)
        : undefined;
    }
  }

  private initLame12(): void {
    const dataLame12 = this.category.additionalMarketingDatas?.find((data) =>
      data.position === CategoryAdditionalMarketingDataPositionEnum.Lame_12 &&
      data.typeSection === CategoryAdditionalMarketingDataTypeEnum.Seo_Module
    );

    const displayLame12 = !!dataLame12 && dataLame12.additionalMarketingPushs?.length > 0;

    const seoParagraphs = displayLame12
      ? dataLame12.additionalMarketingPushs.map((data) => {
        return this.pushCardMapper.categoryAdditionalMarketingDatatoISeoParagraph(data);
      })
      : undefined;

    this.seo = {
      title: dataLame12?.headline,
      titleLevel: 2,
      paragraphs: seoParagraphs
    };
  }

  showBrandCornerBanner(brandCorner: string, categoryName: string): void {
    this.isBannerVisible = true;

    this.bannerData = {
      siteBrand: this.brandSite,
      brand: brandCorner,
      itemName: categoryName,
      website: this.windowRef.location.hostname
    };
  }

  bannerVisibilityChange(): void {
    this.isBannerVisible = false;
  }

  private getMediaContainer(media: CustomMedia): Observable<CustomMedia> {
    return this.imageService.getMediaFromContainer(media as MediaContainer);
  }

  private getMediaFromAdditionalMarketing(mediaContainer: CustomMedia[]): CustomMedia {
    return mediaContainer.reduce((media, currentMedia) => {
      const m = { ...media, [currentMedia.format]: { ...currentMedia } };
      delete m.format;
      return m;
    }, {});
  }
}
